import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, LogOut, ArrowLeft, Star, Edit, Trash2, Search, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

import { BACKEND_URL } from '../config/api';

export default function AdminReviewManagement() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [editFormData, setEditFormData] = useState({
    punctuality: 5,
    professionalism: 5,
    quality: 5,
    comment: ''
  });

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    filterReviews();
  }, [searchQuery, reviews]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      
      if (response.data.user_type !== 'admin') {
        navigate('/login');
        return;
      }
      
      setUser(response.data);
      fetchReviews();
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/reviews`, {
        withCredentials: true
      });
      setReviews(response.data);
      setFilteredReviews(response.data);
    } catch (error) {
      toast.error('Failed to fetch reviews');
    } finally {
      setLoading(false);
    }
  };

  const filterReviews = () => {
    if (!searchQuery) {
      setFilteredReviews(reviews);
      return;
    }
    
    const query = searchQuery.toLowerCase();
    const filtered = reviews.filter(r => 
      r.tradesperson_name?.toLowerCase().includes(query) ||
      r.employer_name?.toLowerCase().includes(query) ||
      r.comment?.toLowerCase().includes(query)
    );
    setFilteredReviews(filtered);
  };

  const handleEditReview = (review) => {
    setSelectedReview(review);
    setEditFormData({
      punctuality: review.punctuality,
      professionalism: review.professionalism,
      quality: review.quality,
      comment: review.comment || ''
    });
    setShowEditModal(true);
  };

  const handleSaveReview = async () => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/reviews/${selectedReview.review_id}`,
        editFormData,
        { withCredentials: true }
      );
      toast.success('Review updated successfully');
      setShowEditModal(false);
      fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update review');
    }
  };

  const handleDeleteReview = async (review) => {
    if (!window.confirm(`Are you sure you want to delete this review from ${review.employer_name} for ${review.tradesperson_name}?`)) {
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/admin/reviews/${review.review_id}`, {
        withCredentials: true
      });
      toast.success('Review deleted successfully');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, {
        withCredentials: true
      });
      navigate('/login');
    } catch (error) {
      navigate('/login');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const StarRating = ({ value, size = 'sm' }) => {
    const sizeClass = size === 'sm' ? 'h-3.5 w-3.5' : 'h-5 w-5';
    return (
      <div className="flex gap-0.5">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`${sizeClass} ${
              i < value ? 'text-orange-500 fill-orange-500' : 'text-slate-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const StarInput = ({ label, value, onChange }) => (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">{label}</label>
      <div className="flex items-center gap-3">
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => onChange(star)}
              className="focus:outline-none"
            >
              <Star
                className={`h-8 w-8 transition-colors ${
                  star <= value
                    ? 'text-orange-500 fill-orange-500'
                    : 'text-slate-300 hover:text-orange-300'
                }`}
              />
            </button>
          ))}
        </div>
        <span className="text-lg font-semibold text-slate-700">{value}/5</span>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="btn-secondary text-sm flex items-center gap-2"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4" />
              Dashboard
            </button>
            <div className="flex items-center gap-2">
              <Star className="h-6 w-6 text-orange-500" />
              <h1 className="text-xl font-bold text-slate-900">Review Management</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">Hi, {user?.name}</span>
            <button
              onClick={handleLogout}
              className="btn-secondary text-sm flex items-center gap-2"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search */}
        <div className="card mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by tradesperson, employer, or comment..."
                className="w-full pl-10"
                data-testid="search-input"
              />
            </div>
          </div>
          <p className="text-sm text-slate-600 mt-2">
            Showing {filteredReviews.length} of {reviews.length} reviews
          </p>
        </div>

        {/* Reviews List */}
        <div className="space-y-4" data-testid="reviews-list">
          {filteredReviews.length === 0 ? (
            <div className="card text-center py-12">
              <Star className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-lg">No reviews found</p>
            </div>
          ) : (
            filteredReviews.map((review) => (
              <div
                key={review.review_id}
                className="card hover:shadow-md transition-shadow"
                data-testid={`review-item-${review.review_id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  {/* Review Info */}
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <div className="flex items-center gap-2 bg-blue-50 px-3 py-1 rounded-full">
                        <User className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-800">
                          {review.tradesperson_name}
                        </span>
                      </div>
                      <span className="text-slate-400">←</span>
                      <div className="flex items-center gap-2 bg-green-50 px-3 py-1 rounded-full">
                        <Building2 className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-medium text-green-800">
                          {review.employer_name}
                        </span>
                      </div>
                      <span className="text-xs text-slate-400 ml-auto">
                        {formatDate(review.created_at)}
                        {review.edited_by_admin && (
                          <span className="ml-2 text-orange-500">(edited by admin)</span>
                        )}
                      </span>
                    </div>

                    {/* Ratings */}
                    <div className="grid grid-cols-3 gap-4 mb-3">
                      <div>
                        <p className="text-xs font-medium text-slate-500 mb-1">Punctuality</p>
                        <StarRating value={review.punctuality} />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-slate-500 mb-1">Professionalism</p>
                        <StarRating value={review.professionalism} />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-slate-500 mb-1">Quality</p>
                        <StarRating value={review.quality} />
                      </div>
                    </div>

                    {/* Comment */}
                    {review.comment && (
                      <p className="text-slate-600 italic text-sm">"{review.comment}"</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 lg:flex-col">
                    <button
                      onClick={() => handleEditReview(review)}
                      className="btn-secondary text-sm flex items-center gap-2"
                      data-testid={`edit-review-${review.review_id}`}
                    >
                      <Edit className="h-4 w-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteReview(review)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors"
                      data-testid={`delete-review-${review.review_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Edit Review Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-orange-500" />
              Edit Review
            </DialogTitle>
          </DialogHeader>
          {selectedReview && (
            <div className="mt-4">
              <div className="bg-slate-50 rounded-lg p-3 mb-4">
                <p className="text-sm text-slate-600">
                  Review for <strong>{selectedReview.tradesperson_name}</strong> by <strong>{selectedReview.employer_name}</strong>
                </p>
              </div>

              <div className="space-y-6">
                <StarInput
                  label="Punctuality"
                  value={editFormData.punctuality}
                  onChange={(v) => setEditFormData({ ...editFormData, punctuality: v })}
                />
                <StarInput
                  label="Professionalism"
                  value={editFormData.professionalism}
                  onChange={(v) => setEditFormData({ ...editFormData, professionalism: v })}
                />
                <StarInput
                  label="Quality of Work"
                  value={editFormData.quality}
                  onChange={(v) => setEditFormData({ ...editFormData, quality: v })}
                />

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Comment</label>
                  <textarea
                    value={editFormData.comment}
                    onChange={(e) => setEditFormData({ ...editFormData, comment: e.target.value })}
                    className="w-full"
                    rows={3}
                    placeholder="Optional comment..."
                    data-testid="edit-comment-input"
                  />
                </div>

                <div className="flex gap-2 pt-4 border-t">
                  <button
                    onClick={handleSaveReview}
                    className="btn-primary flex-1"
                    data-testid="save-review-btn"
                  >
                    Save Changes
                  </button>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="btn-secondary"
                    data-testid="cancel-edit-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
